import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IconSparkles } from '@tabler/icons-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// The 10 personal profile fields.
const PROFILE_FIELDS = [
  'name',
  'email',
  'address',
  'district',
  'thana',
  'postcode',
  'gender',
  'about',
  'profession',
  'dateOfBirth',
];

// Business ("Dokan") fields of the user's FIRST/default office. The backend flattens
// that office's business profile onto the user, so these read straight off `user`.
// `services` is counted separately (needs at least one non-empty entry). This mirrors
// the backend's ProfileCompletionService — both must agree on what earns the bonus.
const BUSINESS_FIELDS = [
  'shopName',
  'businessCategory',
  'businessAddress',
  'shopPhone',
  'businessAbout',
  'shopLogo',
  'mapUrl',
  'facebookUrl',
];

const isFilled = (v) => v !== null && v !== undefined && v !== '';
const hasService = (v) => Array.isArray(v) && v.some((s) => String(s ?? '').trim() !== '');

// Total counted fields = 10 personal + 8 business + 1 for "at least one service".
export const PROFILE_COMPLETION_TOTAL = PROFILE_FIELDS.length + BUSINESS_FIELDS.length + 1;

// Operators don't own a shop (they're added to the owner's), so for them only the personal
// fields count — the shop / business-profile fields never apply.
const isOperatorUser = (user) => user?.role === 'OPERATOR';

export function calcCompletion(user) {
  const total = isOperatorUser(user) ? PROFILE_FIELDS.length : PROFILE_COMPLETION_TOTAL;
  if (!user) return { percent: 0, filled: 0, remaining: total, total };
  // Email counts only once it's VERIFIED (a filled-but-unverified email doesn't earn
  // the bonus) — mirrors the backend ProfileCompletionService rule.
  let filled = PROFILE_FIELDS.filter((f) =>
    f === 'email' ? (isFilled(user.email) && !!user.emailVerified) : isFilled(user[f])
  ).length;
  if (!isOperatorUser(user)) {
    filled += BUSINESS_FIELDS.filter((f) => isFilled(user[f])).length;
    if (hasService(user.services)) filled += 1;
  }
  return {
    percent: Math.round((filled / total) * 100),
    filled,
    remaining: total - filled,
    total,
  };
}

// i18n label key for every counted field — lets the /profile banner name exactly
// which fields are still empty. All labels already exist (form/summary labels).
export const FIELD_LABEL_KEYS = {
  name: 'common.name',
  email: 'profile.email',
  address: 'profile.address',
  district: 'profile.district',
  thana: 'profile.thana',
  postcode: 'profile.postcode',
  gender: 'profile.gender',
  about: 'profile.about',
  profession: 'profile.profession',
  dateOfBirth: 'profile.dateOfBirth',
  shopName: 'profile.shopName',
  businessCategory: 'profile.businessCategory',
  businessAddress: 'profile.businessAddress',
  shopPhone: 'profile.shopPhone',
  businessAbout: 'profile.businessAbout',
  shopLogo: 'profile.shopLogo',
  mapUrl: 'profile.mapUrl',
  facebookUrl: 'profile.facebookUrl',
  services: 'profile.services',
};

// Field keys still missing, grouped by profile section (personal tab vs shop tab)
// — the same rules calcCompletion counts with (email only counts once verified).
export function getMissingFields(user) {
  if (!user) return { personal: [...PROFILE_FIELDS], shop: [...BUSINESS_FIELDS, 'services'] };
  const personal = PROFILE_FIELDS.filter((f) =>
    f === 'email' ? !(isFilled(user.email) && !!user.emailVerified) : !isFilled(user[f])
  );
  // Operators have no shop of their own — never list shop fields as missing.
  if (isOperatorUser(user)) return { personal, shop: [] };
  const shop = BUSINESS_FIELDS.filter((f) => !isFilled(user[f]));
  if (!hasService(user.services)) shop.push('services');
  return { personal, shop };
}

// Exposed so other surfaces (e.g. the profile dropdown) can decide whether to
// render the completion row without duplicating the field logic.
export function getProfileCompletion(user) {
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // Operators don't earn the profile-completion bonus, so never nudge them.
  const isOperator = user?.role === 'OPERATOR';
  // Once the bonus has been redeemed there's nothing left to earn — stop nudging
  // outside the profile page (the /profile page banner is the only surface left).
  const bonusGiven = user?.settings?.profileCompletionBonusGiven === true;
  const { percent, remaining } = calcCompletion(user);
  return { percent, remaining, incomplete: !!user && !isAdmin && !isOperator && !bonusGiven && percent < 100 };
}

// Full-width completion card — used on the /profile page (personal + shop tabs) and
// on the FIRST/default shop's settings page. Shows the percent bar plus the exact
// missing fields, grouped and tinted by section (blue = personal, orange = shop).
// Self-hides for admins and once the profile hits 100%.
export function ProfileCompletionCard() {
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const toBn = (num) => (isBn ? String(num).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]) : String(num));
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isOperator = user?.role === 'OPERATOR';
  const { percent, remaining } = calcCompletion(user);
  if (!user || isAdmin || percent >= 100) return null;
  const barTone = percent < 40
    ? { text: 'text-red-600 dark:text-red-400', bar: 'bg-red-500', iconBg: 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400' }
    : percent < 75
    ? { text: 'text-orange-600 dark:text-orange-400', bar: 'bg-primary', iconBg: 'bg-orange-50 dark:bg-orange-950/30 text-primary' }
    : { text: 'text-emerald-600 dark:text-emerald-400', bar: 'bg-emerald-500', iconBg: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' };
  const missing = getMissingFields(user);
  // Section-tinted pills so a glance tells the user WHERE each remaining field lives.
  const groups = [
    {
      key: 'personal',
      label: t('profile.personalInformation'),
      fields: missing.personal,
      pill: 'bg-blue-50 dark:bg-blue-950/30 border-blue-100 dark:border-blue-900/40 text-blue-600 dark:text-blue-400',
    },
    {
      key: 'shop',
      label: t('profile.shopTitle'),
      fields: missing.shop,
      pill: 'bg-orange-50 dark:bg-orange-950/30 border-orange-100 dark:border-orange-900/40 text-orange-600 dark:text-orange-400',
    },
  ].filter((g) => g.fields.length);
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm border border-zinc-200 dark:border-zinc-700 p-3.5 sm:p-4 mb-3 sm:mb-4">
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${barTone.iconBg}`}>
            <IconSparkles size={18} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-[14px] leading-tight text-zinc-800 dark:text-zinc-100">{t('profileCompletion.pageTitle')}</span>
              {!isOperator && !user?.settings?.profileCompletionBonusGiven && (
                <span className="text-[10.5px] font-semibold px-1.5 py-px rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/40">
                  {t('profileCompletion.bonusTag', { amount: user?.profileCompletionBonus ?? 20 })}
                </span>
              )}
            </div>
            <p className="text-[11px] leading-snug text-zinc-500 dark:text-zinc-400 mt-0.5">
              {isOperator || user?.settings?.profileCompletionBonusGiven
                ? t('profileCompletion.pageSubtitle', { remaining })
                : t('profileCompletion.pageSubtitleSimple', { remaining, amount: user?.profileCompletionBonus ?? 20 })}
            </p>
          </div>
        </div>
        <span className={`font-extrabold text-xl tabular-nums flex-shrink-0 ${barTone.text}`}>{toBn(percent)}%</span>
      </div>
      <div className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barTone.bar}`} style={{ width: `${percent}%` }} />
      </div>
      {groups.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 flex-shrink-0">
            {t('profileCompletion.missingLabel')}:
          </span>
          {groups.map((g) => (
            <span key={g.key} className="inline-flex flex-wrap items-center gap-1">
              <span className="text-[10.5px] text-zinc-400 dark:text-zinc-500">{g.label}</span>
              {g.fields.map((f) => (
                <span key={f} className={`text-[10.5px] font-medium px-1.5 py-px rounded-full border ${g.pill}`}>
                  {t(FIELD_LABEL_KEYS[f])}
                </span>
              ))}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfileCompletionBanner({ mobile = false, menu = false, authReady = false, onClose = null }) {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [closed, setClosed] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const { percent, remaining } = calcCompletion(user);
  const bonusAlreadyGiven = user?.settings?.profileCompletionBonusGiven === true;
  const bonusAmount = user?.profileCompletionBonus ?? 20;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // Operators get no profile-completion bonus — hide the banner entirely for them.
  const isOperator = user?.role === 'OPERATOR';
  const ringColor = percent < 40 ? '#fa5252' : percent < 75 ? '#f0910a' : '#40c057';

  // Menu variant — compact row designed to sit inside the profile dropdown.
  // Only renders when the profile is actually incomplete.
  if (menu) {
    if (!user || isAdmin || isOperator || bonusAlreadyGiven || percent >= 100) return null;
    const ringSize = 30;
    const stroke = 3;
    const r = (ringSize - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const dashOffset = circ * (1 - percent / 100);
    return (
      <div className="font-hind relative flex items-center gap-2.5 mx-1 my-1 rounded-lg border border-primary-200/70 bg-gradient-to-r from-primary-50 to-primary-100/60 px-2.5 py-2 transition-colors hover:from-primary-100 hover:to-primary-100 dark:border-primary-900/40 dark:from-primary-900/40 dark:to-primary-900/20 dark:hover:from-primary-900/40">
        <Link to="/profile" className="flex min-w-0 flex-1 items-center gap-2.5 no-underline">
          <span className="relative inline-flex shrink-0 items-center justify-center">
            <svg width={ringSize} height={ringSize} className="-rotate-90">
              <circle cx={ringSize / 2} cy={ringSize / 2} r={r} fill="none" className="stroke-primary-200 dark:stroke-primary-900/50" strokeWidth={stroke} />
              <circle
                cx={ringSize / 2} cy={ringSize / 2} r={r} fill="none" stroke={ringColor} strokeWidth={stroke}
                strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={dashOffset}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-extrabold tabular-nums" style={{ color: ringColor }}>
              {percent}
            </span>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-bold leading-tight text-primary-700 dark:text-primary-300">
              {t('profileCompletion.title')}
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-primary-600/80 dark:text-primary-400/80">
                {t('profileCompletion.mobileSubtitle', { remaining })}
              </span>
              {!bonusAlreadyGiven && (
                <span className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {t('profileCompletion.bonusTag', { amount: bonusAmount })}
                </span>
              )}
            </span>
          </span>
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            className="shrink-0 cursor-pointer border-0 bg-transparent p-0 pl-1 text-base leading-none text-primary-600/70 hover:text-primary-700 dark:text-primary-400/70"
            aria-label="Close"
          >
            ×
          </button>
        )}
      </div>
    );
  }

  // Hidden by default — only show after server-verified user data is ready.
  // This prevents the flash where stale localStorage data briefly shows the banner
  // for users who have already completed their profile.
  if (!authReady || !user || closed || percent >= 100) return null;
  // Bonus already redeemed → the header/drawer nudges disappear; only the banner
  // on the /profile page keeps tracking the remaining fields.
  if (isAdmin || isOperator || bonusAlreadyGiven) return null;

  if (mobile) {
    return (
      <div className="font-hind rounded-lg border border-zinc-200 bg-white px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-900">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[13px] font-bold leading-tight text-primary-700 dark:text-primary-400">
              {t('profileCompletion.mobileTitle')}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-primary-600/80 dark:text-primary-400/80">
                {t('profileCompletion.mobileSubtitle', { remaining })}
              </span>
              {!bonusAlreadyGiven && (
                <span className="shrink-0 rounded-full bg-emerald-500 px-1.5 py-px text-[11px] font-bold text-white">
                  {t('profileCompletion.bonusTag', { amount: bonusAmount })}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-sm font-bold" style={{ color: ringColor }}>{percent}%</span>
            <button
              className="cursor-pointer border-0 bg-transparent p-0 text-base leading-none text-primary-600 dark:text-primary-400"
              onClick={() => setClosed(true)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
        <div className="mb-2 h-1.5 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
          <div
            className="h-full rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${percent}%`, background: ringColor }}
          />
        </div>
        <div
          className={`mb-2 text-[11px] leading-snug ${
            bonusAlreadyGiven ? 'text-zinc-500 dark:text-zinc-400' : 'font-bold text-emerald-600'
          }`}
        >
          {bonusAlreadyGiven
            ? t('profileCompletion.hint')
            : t('profileCompletion.hintBonus', { amount: bonusAmount })}
        </div>
        <Link to="/profile" className="block text-xs font-bold text-primary-600 no-underline dark:text-primary-400">
          {t('profileCompletion.fillNow')}
        </Link>
      </div>
    );
  }

  // Desktop — tiny self-explanatory badge: circular progress ring + bonus tag.
  // The full label is shown on hover via a tooltip.
  const ringSize = 26;
  const stroke = 3;
  const r = (ringSize - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dashOffset = circ * (1 - percent / 100);

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`font-hind inline-flex max-w-fit shrink-0 items-center gap-1.5 rounded-full border border-primary-200 bg-gradient-to-r from-primary-50 to-primary-100 py-0.5 pl-1 pr-1.5 shadow-sm dark:border-primary-900/40 dark:from-primary-900/40 dark:to-primary-900/30 ${
              shake ? 'shake' : ''
            }`}
          >
            <Link
              to="/profile"
              className="inline-flex items-center gap-1.5 no-underline"
              aria-label={t('profileCompletion.title')}
            >
              <span className="relative inline-flex items-center justify-center">
                <svg width={ringSize} height={ringSize} className="-rotate-90">
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={r}
                    fill="none"
                    className="stroke-primary-200 dark:stroke-primary-900/50"
                    strokeWidth={stroke}
                  />
                  <circle
                    cx={ringSize / 2}
                    cy={ringSize / 2}
                    r={r}
                    fill="none"
                    stroke={ringColor}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circ}
                    strokeDashoffset={dashOffset}
                    className="transition-[stroke-dashoffset] duration-500 ease-out"
                  />
                </svg>
                <span
                  className="absolute inset-0 flex items-center justify-center text-[8.5px] font-extrabold tabular-nums tracking-tight"
                  style={{ color: ringColor }}
                >
                  {percent}
                </span>
              </span>
              {!bonusAlreadyGiven && (
                <span className="rounded-full bg-emerald-500 px-1.5 py-px text-[9.5px] font-bold leading-relaxed text-white">
                  {t('profileCompletion.bonusTag', { amount: bonusAmount })}
                </span>
              )}
            </Link>
            <button
              className="cursor-pointer border-0 bg-transparent px-0 pl-0.5 text-sm leading-none text-primary-600/70 hover:text-primary-700 dark:text-primary-400/70"
              onClick={() => setClosed(true)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="font-hind border-0 bg-primary-600 text-white dark:bg-primary-600 dark:text-white">
          {t('profileCompletion.title')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
